import { Injectable } from '@angular/core';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { SharedService } from '../../shared/_services/shared.service';

@Injectable({
  providedIn: 'root',
})
export class VisitorService {
  private readonly TRIAL_KEY = 'fpjs_trial_data';
  private readonly TRIAL_DURATION_DAYS = 5;

  private subscriptionPlan: string | null = null;
  private subscriptionExpiresAt: number | null = null;

  private visitorIdCache: string | null = null;
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;

  constructor(private sharedService: SharedService) { }

  private async initSubscriptionInfo(): Promise<void> {
    if (this.initialized) return;

    if (this.initializationPromise) {
      await this.initializationPromise;
      return;
    }

    this.initializationPromise = this.initializeVisitorInfo()
    await this.initializationPromise;
  }

  private async initializeVisitorInfo(): Promise<void> {
    const visitorId = await this.getVisitorId();

    if (visitorId === 'fallback-id' || visitorId === 'ssr-fallback-id') {
      // console.warn('Could not identify visitor properly. Skipping trial tracking.');
      this.initialized = true;
      return;
    }

    await this.identifyGuest(visitorId);
    this.initialized = true;
  }

  public async getTrialStatus(): Promise<'advance' | 'none'> {
    await this.initSubscriptionInfo();
    const now = Date.now();
    if (this.subscriptionPlan && this.subscriptionExpiresAt) {
      if (this.subscriptionExpiresAt > now) {
        return this.subscriptionPlan.toLowerCase() as 'advance';
      }
      return 'none';
    }

    return this.fallbackTrialLogic();
  }

  public async getSubscriptionInfo(): Promise<{ plan: string; daysLeft: number }> {
    await this.initSubscriptionInfo();

    const now = Date.now();
    const plan = this.subscriptionPlan || 'none';
    const expiryTime = this.subscriptionExpiresAt ?? 0;
    const daysLeft = Math.max(Math.ceil((expiryTime - now) / (1000 * 60 * 60 * 24)), 0);

    return { plan, daysLeft };
  }

  private async fallbackTrialLogic(): Promise<'advance' | 'none'> {
    const visitorId = await this.getVisitorId();

    let data: Record<string, { startedAt: string }> = {};
    const raw = localStorage.getItem(this.TRIAL_KEY);
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch {
        console.warn('Corrupt trial data, resetting...');
        localStorage.removeItem(this.TRIAL_KEY);
      }
    }

    if (!data[visitorId]) {
      data[visitorId] = { startedAt: new Date().toISOString() };
      localStorage.setItem(this.TRIAL_KEY, JSON.stringify(data));
    }

    const startedAt = data[visitorId]?.startedAt;
    const startTime = new Date(startedAt).getTime();
    const now = Date.now();

    if (isNaN(startTime)) {
      console.warn('Invalid start time detected. Resetting trial.');
      data[visitorId] = { startedAt: new Date().toISOString() };
      localStorage.setItem(this.TRIAL_KEY, JSON.stringify(data));
      return 'advance';
    }

    const daysPassed = Math.floor((now - startTime) / (1000 * 60 * 60 * 24));
    return daysPassed < this.TRIAL_DURATION_DAYS ? 'advance' : 'none';
  }

  private async identifyGuest(visitorId: string): Promise<boolean> {
    try {
      return await new Promise<boolean>((resolve) => {
        this.sharedService.guestIdentify(visitorId).subscribe({
          next: (res) => {
            const data = res?.data;
            if (data?.subscriptionPlan && data?.subscriptionExpiresAt) {
              this.subscriptionPlan = data.subscriptionPlan;
              this.subscriptionExpiresAt = data.subscriptionExpiresAt;
              resolve(true);
            } else {
              resolve(false);
            }
          },
          error: (err) => {
            console.error('identifyGuest API error', err);
            resolve(false);
          },
        });
      });
    } catch (e) {
      console.error('Unexpected error in identifyGuest:', e);
      return false;
    }
  }

  public async getVisitorId(): Promise<string> {
    if (this.visitorIdCache) return this.visitorIdCache;

    if (typeof window === 'undefined') return 'ssr-fallback-id';

    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      this.visitorIdCache = result.visitorId;
      return result.visitorId;
    } catch (error) {
      console.error('FingerprintJS failed to load:', error);
      return 'fallback-id';
    }
  }
}